import { useState, useEffect, useRef, useMemo, use } from 'react';
import { usePlacesByPassageData } from '@/data/places/places-by-passage-data-context';
import { usePlacesData } from '@/data/places/places-data-context';
import { getBestTransitionImageFromPlaces } from '@/data/places/place-images-fetch';
import { HistoricalEventsByPassageDataContext } from '@/data/historical-events/historical-events-by-passage-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import defaultHeroImage from '@/assets/hero7.png';

/**
 * Hook to get the best transition image for a passage
 * Uses hydrated Place data from PlacesDataContext (no Firestore fetch needed!)
 * Preloads images in the background when the passage loads
 * Returns both ruins and reconstruction images
 *
 * IMPORTANT: This hook immediately knows if images exist (synchronous from hydrated data)
 * and only waits for the actual image files to download (async preloading)
 */
export function usePlaceTransitionImage(passageId: string | null) {
  const { getPlacesByPassageByPassageId } = usePlacesByPassageData();
  const { getPlaceByName } = usePlacesData();
  const { getHistoricalEventsByPassageByPassageId } = use(HistoricalEventsByPassageDataContext);
  const passageContext = use(PassageDataContext);
  const [isReady, setIsReady] = useState(false);
  const currentPassageRef = useRef<string | null>(null);

  // Synchronously get image URLs from hydrated Place data (no async needed!)
  const imageData = useMemo(() => {
    if (!passageId) {
      return {
        ruinsImageUrl: defaultHeroImage,
        reconstructionImageUrl: defaultHeroImage,
        placeName: null,
        hasImages: false
      };
    }

    const placesByPassage = getPlacesByPassageByPassageId(passageId);
    if (!placesByPassage) {
      return {
        ruinsImageUrl: defaultHeroImage,
        reconstructionImageUrl: defaultHeroImage,
        placeName: null,
        hasImages: false
      };
    }

    // Get the best images from hydrated Place data (synchronous!)
    const bestTransitionImage = getBestTransitionImageFromPlaces(
      placesByPassage,
      getPlaceByName,
      getHistoricalEventsByPassageByPassageId,
      passageContext.get
    );

    if (bestTransitionImage) {
      return {
        ruinsImageUrl: bestTransitionImage.ruinsImageUrl,
        reconstructionImageUrl: bestTransitionImage.reconstructionImageUrl,
        placeName: bestTransitionImage.placeName,
        hasImages: true
      };
    }

    return {
      ruinsImageUrl: defaultHeroImage,
      reconstructionImageUrl: defaultHeroImage,
      placeName: null,
      hasImages: false
    };
  }, [passageId, getPlacesByPassageByPassageId, getPlaceByName]);

  // Preload images in the background (async)
  useEffect(() => {
    // Reset ready state when passage changes
    if (currentPassageRef.current !== passageId) {
      setIsReady(false);
      currentPassageRef.current = passageId;
    }

    // If no images exist, mark as ready immediately (default images are always available)
    if (!imageData.hasImages) {
      setIsReady(true);
      return;
    }

    // Preload the actual image files in the background
    const loadImages = async () => {
      try {
        const ruinsImg = new Image();
        const reconstructionImg = new Image();
        ruinsImg.src = imageData.ruinsImageUrl;
        reconstructionImg.src = imageData.reconstructionImageUrl;

        await Promise.all([
          new Promise<void>((resolve, reject) => {
            ruinsImg.onload = () => resolve();
            ruinsImg.onerror = () => reject();
            setTimeout(() => reject(new Error('Ruins image load timeout')), 10000);
          }),
          new Promise<void>((resolve, reject) => {
            reconstructionImg.onload = () => resolve();
            reconstructionImg.onerror = () => reject();
            setTimeout(() => reject(new Error('Reconstruction image load timeout')), 10000);
          })
        ]);

        setIsReady(true); // Images successfully preloaded
      } catch (error) {
        console.error('Error preloading place transition images:', error);
        setIsReady(true); // Mark as ready anyway - we have the URLs even if preload failed
      }
    };

    loadImages();
  }, [imageData]);

  return {
    ruinsImageUrl: imageData.ruinsImageUrl,
    reconstructionImageUrl: imageData.reconstructionImageUrl,
    placeName: imageData.placeName,
    isReady
  };
}
